import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TemplatesService } from '../../../services/templates.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TemplateFolder } from '../../../models/TemplateFolder';
import { TableAction } from '../../../shared/components/table/table.component';

@Component({
  selector: 'app-templates',
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.css']
})
export class TemplatesComponent implements OnInit, OnDestroy {
  folders: TemplateFolder[] = [];
  isLoading = false;
  showCreateModal = false;
  createForm: FormGroup;
  isCreating = false;
  editingFolder: TemplateFolder | null = null;

  private destroy$ = new Subject<void>();

  columns = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Área', label: 'area' },
    { titulo: 'Observação', label: 'observation' },
    { titulo: 'Status', label: 'isActive', formatter: (value: boolean) => value ? 'Ativo' : 'Inativo' }
  ];

  actions: TableAction[] = [
    {
      label: 'Abrir',
      icon: 'fa-solid fa-folder-open',
      action: 'open',
      class: 'btn-primary',
      visible: true
    },
    {
      label: 'Editar',
      icon: 'fa-solid fa-edit',
      action: 'edit',
      class: 'btn-edit',
      visible: true
    }
  ];

  constructor(
    private router: Router,
    private templatesService: TemplatesService,
    private errorService: ErrorService,
    private fb: FormBuilder
  ) {
    this.createForm = this.createFormGroup();
  }

  private createFormGroup(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      area: ['', [Validators.required, Validators.minLength(2)]],
      observation: [''],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadFolders();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFolders(): void {
    this.isLoading = true;
    this.templatesService.listFolders()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.folders = res;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Erro ao carregar pastas:', error);
          this.isLoading = false;
          this.errorService.showErrorMessage('Erro ao carregar pastas. Tente novamente.');
        }
      });
  }

  onSortChange(event: { sortBy: string; sortOrder: 'asc' | 'desc' }): void {
    this.folders = [...this.folders].sort((a, b) => {
      const va = String((a as any)[event.sortBy] ?? '');
      const vb = String((b as any)[event.sortBy] ?? '');
      const cmp = va.localeCompare(vb, 'pt-BR', { numeric: true });
      return event.sortOrder === 'asc' ? cmp : -cmp;
    });
  }

  onActionClick(event: { action: string; item: TemplateFolder }): void {
    switch (event.action) {
      case 'open':
        this.openFolder(event.item);
        break;
      case 'edit':
        this.openEditModal(event.item);
        break;
      default:
        break;
    }
  }

  openFolder(folder: TemplateFolder): void {
    this.router.navigate(['/documents/templates', folder.id]);
  }

  openCreateModal(): void {
    this.createForm.reset({ isActive: true });
    this.showCreateModal = true;
    this.editingFolder = null;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
    this.createForm.reset();
    this.editingFolder = null;
  }

  openEditModal(folder: TemplateFolder): void {
    this.editingFolder = folder;
    this.createForm.reset();
    this.createForm.patchValue({
      name: folder.name,
      area: folder.area,
      observation: folder.observation || '',
      isActive: folder.isActive
    });
    this.showCreateModal = true;
  }

  submitForm(): void {
    if (!this.createForm.valid || this.isCreating) {
      return;
    }

    this.isCreating = true;
    const formData = this.createForm.value;

    if (this.editingFolder) {
      this.updateFolder(formData);
    } else {
      this.createFolder(formData);
    }
  }

  private updateFolder(formData: any): void {
    this.templatesService.updateFolder(this.editingFolder!.id, formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          const idx = this.folders.findIndex(f => f.id === updated.id);
          if (idx > -1) {
            this.folders[idx] = updated;
          }
          this.closeCreateModal();
          this.errorService.showInfoMessage('Pasta atualizada com sucesso!');
        },
        error: (error) => {
          console.error('Erro ao atualizar pasta:', error);
          this.errorService.showErrorMessage('Erro ao atualizar pasta. Tente novamente.');
        },
        complete: () => {
          this.isCreating = false;
        }
      });
  }

  private createFolder(formData: any): void {
    this.templatesService.createFolder(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (newFolder) => {
          this.folders.push(newFolder);
          this.closeCreateModal();
          this.errorService.showInfoMessage('Pasta criada com sucesso!');
        },
        error: (error) => {
          console.error('Erro ao criar pasta:', error);
          this.errorService.showErrorMessage('Erro ao criar pasta. Tente novamente.');
        },
        complete: () => {
          this.isCreating = false;
        }
      });
  }
}


